import { Tooltip, Box, type TooltipProps } from '@mui/material';
import { CELESTE_URUGUAY } from '../theme/conmemoracion';

/**
 * El desplegable que explica por qué el logo está celeste.
 *
 * Va en un componente propio porque lo usan tres lugares —navbar y footer del sitio público
 * y el logo del CRM— y el contenido tiene que decir lo mismo en los tres. Con el `title`
 * nativo del navegador no alcanzaba: tarda un segundo largo en aparecer, no se puede
 * formatear, y en el CRM desentonaba con el resto de los tooltips, que son de MUI.
 */
export function TooltipConmemoracion({
  titulo, detalle, bandera, children, placement = 'bottom-start',
}: {
  titulo: string;
  detalle: string;
  bandera: string;
  children: TooltipProps['children'];
  placement?: TooltipProps['placement'];
}) {
  return (
    <Tooltip
      arrow
      placement={placement}
      // Aparece enseguida: es una explicación breve de algo que ya se está viendo, no una
      // ayuda que haya que ir a buscar.
      enterDelay={150}
      title={(
        <Box sx={{ px: 0.5, py: 0.75, textAlign: 'center' }}>
          <Box sx={{ fontSize: 26, lineHeight: 1, mb: 0.75 }} aria-hidden>{bandera}</Box>
          <Box sx={{ fontSize: 13, fontWeight: 700, lineHeight: 1.35 }}>{titulo}</Box>
          <Box sx={{ fontSize: 11.5, opacity: 0.75, mt: 0.4, lineHeight: 1.4 }}>{detalle}</Box>
        </Box>
      )}
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'rgba(12,20,34,.97)',
            border: `1px solid ${CELESTE_URUGUAY}55`,
            borderRadius: 2,
            maxWidth: 260,
            boxShadow: `0 10px 30px rgba(0,0,0,.45), 0 0 18px ${CELESTE_URUGUAY}22`,
          },
        },
        arrow: { sx: { color: 'rgba(12,20,34,.97)', '&::before': { border: `1px solid ${CELESTE_URUGUAY}55` } } },
      }}
    >
      {children}
    </Tooltip>
  );
}

/**
 * Envuelve al logo con la explicación SOLO cuando la conmemoración está activa.
 *
 * Existe para que cada lugar de uso no tenga que repetir el ternario: sin esto, el logo
 * quedaría dentro de un `<Tooltip>` los 365 días —con su listener de hover y su div— para
 * mostrar un cartel vacío 364 de ellos.
 */
export function ConLaExplicacion({
  conmemoracion, children, placement,
}: {
  conmemoracion: { titulo: string; detalle: string; bandera: string } | null;
  children: TooltipProps['children'];
  placement?: TooltipProps['placement'];
}) {
  if (!conmemoracion) return children;
  return (
    <TooltipConmemoracion
      titulo={conmemoracion.titulo}
      detalle={conmemoracion.detalle}
      bandera={conmemoracion.bandera}
      placement={placement}
    >
      {children}
    </TooltipConmemoracion>
  );
}
